import { ChangeDetectionStrategy, Component, OnInit, inject, input, output } from '@angular/core';

import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { LocationSubset } from '@era/stores';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { SyncTypeFilter } from './devices-sync-filter';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'era-devices-sync-modal',
  templateUrl: './devices-sync-modal.component.html',
  imports: [ReactiveFormsModule, NgbTooltip, FaIconComponent],
})
export class DevicesSyncModalComponent implements OnInit {
  private readonly formBuilder = inject(UntypedFormBuilder);

  public readonly locationSubset = input.required<LocationSubset>();
  public readonly userIsAdmin = input.required<boolean>();
  public readonly closeModal = output();
  public readonly submitFilters = output<{
    filter: SyncTypeFilter;
    force: boolean;
  }>();

  public readonly syncDevicesForm: UntypedFormGroup;
  public readonly LocationSubset = LocationSubset;
  public selectedAllSettings = false;

  private subscriptions: Subscription[] = [];

  constructor() {
    const controlsConfig: SyncTypeFilter & { ForceSync: boolean } = {
      Applications: false,
      NotificationProfiles: false,
      WifiProfiles: false,
      EraApplicationConfiguration: false,
      AppLockProfile: false,
      RestrictionsProfiles: false,
      DeviceName: false,
      AirplaySecurityProfile: false,
      ConfigurationProfiles: false,
      ForceSync: false,
    };

    this.syncDevicesForm = this.formBuilder.group(controlsConfig);
    this.syncDevicesForm.setValidators(this.validateCheckboxes());
  }

  ngOnInit(): void {
    // Disable WifiProfiles checkbox for non-admin users
    if (!this.userIsAdmin()) {
      this.syncDevicesForm.get('WifiProfiles')?.disable();
    }
  }

  validateCheckboxes(): (x: UntypedFormGroup) => { requireOneDeviceModelChecked: boolean } | null {
    return (formGroup: UntypedFormGroup) => {
      let checked = 0;

      Object.keys(formGroup.controls).forEach((key) => {
        const control = formGroup.controls[key];
        if (control.value && key !== 'ForceSync') {
          checked += 1;
        }
      });

      if (checked === 0) {
        return { requireOneDeviceModelChecked: true };
      }

      return null;
    };
  }

  toggleSelectAll() {
    this.selectedAllSettings = !this.selectedAllSettings;
    Object.keys(this.syncDevicesForm.controls).forEach((controlName) => {
      if (controlName !== 'ForceSync') {
        this.syncDevicesForm.controls[controlName].setValue(this.selectedAllSettings);
      }
    });
  }

  closingModal() {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
    this.closeModal.emit();
  }

  onSubmit() {
    const forceSync = this.syncDevicesForm.get('ForceSync')?.value;

    const syncTypeFilter: SyncTypeFilter = {
      Applications: this.syncDevicesForm.get('Applications')?.value,
      NotificationProfiles: this.syncDevicesForm.get('NotificationProfiles')?.value,
      WifiProfiles: this.syncDevicesForm.get('WifiProfiles')?.value,
      EraApplicationConfiguration: this.syncDevicesForm.get('EraApplicationConfiguration')?.value,
      AppLockProfile: this.syncDevicesForm.get('AppLockProfile')?.value,
      RestrictionsProfiles: this.syncDevicesForm.get('RestrictionsProfiles')?.value,
      DeviceName: this.syncDevicesForm.get('DeviceName')?.value,
      AirplaySecurityProfile: this.syncDevicesForm.get('AirplaySecurityProfile')?.value,
      ConfigurationProfiles: this.syncDevicesForm.get('ConfigurationProfiles')?.value,
    };

    this.submitFilters.emit({ filter: syncTypeFilter, force: forceSync });
    this.syncDevicesForm.reset();

    this.closingModal();
  }
}
